<template>
    <div v-if="additionals">
        <div v-if="model && additionals.salaVacinacaoLogada && !!additionals.salaVacinacaoLogada.id">
            <rkm-form ref="RkmFormDistribuicao" show-legend>
                <rkm-area title="Dados da distribuição" type="fieldset">
                    <rkm-row>
                        <rkm-col cols="6">
                            <rkm-form-select :disabled="additionals && !additionals.salaVacinacaoLogada.responsavel"
                                :options="unidadesDe" :validations="validations.unidadeIdDe"
                                @change="handleUnidadeIdDeChange" label="Unidade distribuidora" show-default-option
                                text-field="nomeFantasia" v-model="model.unidadeIdDe" value-field="id" />
                        </rkm-col>

                        <rkm-col cols="6">
                            <rkm-form-select
                                :disabled="!model.unidadeIdDe || (additionals && !additionals.salaVacinacaoLogada.responsavel)"
                                :options="salasVacinacaoDe" :validations="validations.salaVacinacaoIdDe"
                                @change="handleSalaVacinacaoIdDeChange" label="Local distribuidor" show-default-option
                                text-field="nome" v-model="model.salaVacinacaoIdDe" value-field="id" />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="6">
                            <rkm-form-select :options="unidadesPara" :validations="validations.unidadeIdPara"
                                @change="handleUnidadeIdParaChange" label="Unidade recebedora" show-default-option
                                text-field="nomeFantasia" v-model="model.unidadeIdPara" value-field="id" />
                        </rkm-col>

                        <rkm-col cols="6">
                            <rkm-form-select :disabled="!model.unidadeIdPara" :options="salasVacinacaoPara"
                                :validations="validations.salaVacinacaoIdPara" label="Local recebedor"
                                show-default-option text-field="nome" v-model="model.salaVacinacaoIdPara"
                                value-field="id" />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="3">
                            <rkm-form-date-picker
                                :max-date="others.moment(additionals.dataAtual, 'YYYY-MM-DD').format('DD/MM/YYYY')"
                                :validations="validations.data" label="Data" v-model="model.data" />
                        </rkm-col>
                    </rkm-row>
                </rkm-area>
            </rkm-form>

            <rkm-form ref="RkmFormVacina">
                <rkm-area title="Vacina" type="fieldset">
                    <rkm-row>
                        <rkm-col cols="12">
                            <rkm-form-input-autocomplete :disabled="!model.salaVacinacaoIdDe"
                                :repository="repositories.vacina" :search-fields="fields.autocomplete"
                                :text-field="item => item.id ? `${autocompleteTextField(item)} - ${item.produtorNome} 
                                    - ${item.apresentacao}` : item.nome"
                                :validations="validations.vacina" :requestQueryStringParams="{distribuicao: true}"
                                @select="handleVacinaSelect" label="Vacina" v-model="local.vacina" />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="2">
                            <rkm-form-select :disabled="!local.vacina" :options="form.lotes"
                                :validations="validations.lote" @change="handleLoteChange" label="Lote"
                                show-default-option v-model="local.lote" />
                        </rkm-col>

                        <rkm-col cols="2">
                            <rkm-form-input disabled label="Estoque" type="number" v-model="local.estoque" />
                        </rkm-col>

                        <rkm-col cols="2">
                            <rkm-form-input :disabled="!local.lote" :validations="validations.quantidade"
                                label="Quantidade" type="number" v-model="local.quantidade" />
                        </rkm-col>

                        <rkm-col cols="auto">
                            <rkm-button :disabled="!local.vacina" :label="false" @click="handleInserirClick"
                                color="primary">
                                Incluir
                            </rkm-button>

                            <rkm-button :disabled="!local.vacina" :label="false" @click="handleLimpar">
                                Limpar
                            </rkm-button>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="12">
                            <rkm-table :fields="fields.table" :items="model.vacinas" ref="RkmTable">
                                <template v-slot:cell(actions)="{ index }">
                                    <rkm-button @click="handleDestroy(index)" color="danger" small title="Excluir">
                                        <rkm-icon icon="trash" />
                                    </rkm-button>
                                </template>
                            </rkm-table>
                        </rkm-col>
                    </rkm-row>
                </rkm-area>
            </rkm-form>
        </div>

        <rkm-alert v-else color="warning">
            A unidade logada não é uma sala de vacinação ou está desativada, favor cadastra-la para efetuar uma
            distribuição.
        </rkm-alert>
    </div>
</template>

<script>
// Enums.
import parametrizacaoDescricaoVacina from '@/enums/imunizacao/parametrizacaoDescricaoVacina'

// Mixins.
import { error, repository, stringCase } from '@/mixins'

// Others.
import moment from 'moment'

// Vuex.
import { mapActions } from 'vuex'

export default {
    computed: {
        fields() {
            const autocomplete = []
            const table = []

            switch (this.additionals.parametrizacao.parametrizacaoDescricaoVacinaId) {
                case this.enums.parametrizacaoDescricaoVacina.DESCRICAO_ABREVIADA:
                    autocomplete.push({ key: 'abreviacao', searchable: true })
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA:
                    autocomplete.push({ key: 'sigla', searchable: true })
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO:
                    autocomplete.push({ key: 'sigla', searchable: true })
                    autocomplete.push({ key: 'nome', searchable: true })
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO_ABREVIADA:
                    autocomplete.push({ key: 'sigla', searchable: true })
                    autocomplete.push({ key: 'abreviacao', searchable: true })
                    break;

                case this.enums.parametrizacaoDescricaoVacina.DESCRICAO_ABREVIADA_DESCRICAO:
                    autocomplete.push({ key: 'abreviacao', searchable: true })
                    autocomplete.push({ key: 'nome', searchable: true })
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO_ABREVIADA_DESCRICAO:
                    autocomplete.push({ key: 'sigla', searchable: true })
                    autocomplete.push({ key: 'abreviacao', searchable: true })
                    autocomplete.push({ key: 'nome', searchable: true })
                    break;

                default:
                    autocomplete.push({ key: 'nome', searchable: true })
                    break;
            }

            switch (this.additionals.parametrizacao.parametrizacaoDescricaoVacinaId) {
                case this.enums.parametrizacaoDescricaoVacina.DESCRICAO_ABREVIADA:
                    table.push({ key: 'vacinaAbreviacao', label: 'Abreviação da vacina' })
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA:
                    table.push({ key: 'vacinaSigla', label: 'Sigla da vacina' })
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO:
                    table.push({ key: 'vacinaSiglaNome', label: 'Sigla e nome da vacina' })
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO_ABREVIADA:
                    table.push({ key: 'vacinaSiglaAbreviacao', label: 'Sigla e abreviação da vacina' })
                    break;

                case this.enums.parametrizacaoDescricaoVacina.DESCRICAO_ABREVIADA_DESCRICAO:
                    table.push({ key: 'vacinaAbreviacaoNome', label: 'Abreviação e nome da vacina' })
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO_ABREVIADA_DESCRICAO:
                    table.push({ key: 'vacinaSiglaAbreviacaoNome', label: 'Sigla, abreviação e nome da vacina' })
                    break;

                default:
                    table.push({ key: 'vacinaNome', label: 'Nome da vacina' })
                    break;
            }

            table.push(
                { key: 'quantidade', label: 'Quantidade' },
                { key: 'lote.text', label: 'Lote' },
                { class: 'col-min text-center', key: 'actions', label: 'Ações' }
            )

            return {
                autocomplete,
                table,
            }
        },
        validations() {
            return {
                unidadeIdDe: { required: true },
                salaVacinacaoIdDe: { required: true },
                unidadeIdPara: { required: true },
                salaVacinacaoIdPara: { required: true },
                data: { required: true },
                vacina: { required: true },
                lote: { required: true },
                quantidade: {
                    required: true,
                    min_value: 1,
                    max_value: this.local.estoque,
                    max: 8,
                },
            }
        },
        unidadesDe() {
            return this.form.unidadesDe.filter(unidadeDe => unidadeDe.id !== this.model.unidadeIdPara)
        },
        salasVacinacaoDe() {
            return this.form.salasVacinacaoDe.filter(salaVacinacaoDe => salaVacinacaoDe.id !== this.model.salaVacinacaoIdPara)
        },
        unidadesPara() {
            return this.form.unidadesPara.filter(unidadePara => unidadePara.id !== this.model.unidadeIdDe)
        },
        salasVacinacaoPara() {
            return this.form.salasVacinacaoPara.filter(salaVacinacaoPara => salaVacinacaoPara.id !== this.model.salaVacinacaoIdDe)
        },
    },
    created() {
        this.repositories = {
            distribuicao: this.getConectaSusRepository('imunizacao.distribuicao'),
            vacina: this.getConectaSusRepository('cadastro.vacina'),
        }
    },
    data: () => ({
        additionals: null,
        enums: { parametrizacaoDescricaoVacina },
        form: null,
        local: {
            vacina: null,
            lote: null,
            estoque: null,
            quantidade: null,
        },
        model: null,
        others: { moment },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            this.repositories.distribuicao.create()
                .then(response => {
                    this.additionals = Object.assign(response.data.additionals)
                    this.form = Object.assign(response.data.form)
                    this.model = Object.assign(response.data.model)
                    this.$emit('loaded', { additionals: this.additionals })
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleUnidadeIdDeChange(value) {
            this.model.salaVacinacaoIdDe = null
            this.resetRkmFormVacina()

            if (value === null) return

            const request = {
                listar: 'salas_vacinacao_de',
                unidadeIdDe: value,
            }

            this.setLoading()

            this.repositories.distribuicao.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.salasVacinacaoDe = Object.assign(response.data)

                    if (this.form.salasVacinacaoDe.length === 1) {
                        const selected = this.form.salasVacinacaoDe[0]
                        this.model.salaVacinacaoIdDe = selected.id
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleSalaVacinacaoIdDeChange() {
            this.resetRkmFormVacina()
        },
        handleUnidadeIdParaChange(value) {
            this.model.salaVacinacaoIdPara = null

            if (value === null) return

            const request = {
                listar: 'salas_vacinacao_para',
                unidadeIdPara: value,
            }

            this.setLoading()

            this.repositories.distribuicao.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.salasVacinacaoPara = Object.assign(response.data)

                    if (this.form.salasVacinacaoPara.length === 1) {
                        const selected = this.form.salasVacinacaoPara[0]
                        this.model.salaVacinacaoIdPara = selected.id
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleVacinaSelect(value) {

            this.local.lote = null
            this.local.estoque = null
            this.local.quantidade = null

            this.getLotes(this.model.salaVacinacaoIdDe, value.id)
        },
        handleLoteChange(value) {
            this.local.estoque = null
            this.local.quantidade = null

            if (this.local.lote === null) return

            this.getEstoque(this.model.salaVacinacaoIdDe, this.local.vacina.id, value.lote, value.dataValidadeLote)
        },
        handleInserirClick() {
            this.$refs.RkmFormVacina.validate()
                .then(valid => {
                    if (valid === false) return

                    const vacina = this.model.vacinas.find(vacina => {
                        return vacina.vacinaId === this.local.vacina.id &&
                            vacina.lote.value.lote === this.local.lote.lote &&
                            vacina.lote.value.dataValidadeLote === this.local.lote.dataValidadeLote
                    })

                    if (vacina) {
                        this.showValidationNotification('A vacina seleciona já foi Incluída.')
                        return
                    }

                    const lote = this.form.lotes.find(lote => {
                        return lote.value.lote === this.local.lote.lote && lote.value.dataValidadeLote === this.local.lote.dataValidadeLote
                    })

                    this.model.vacinas.push({
                        vacinaId: this.local.vacina.id,
                        vacinaNome: this.local.vacina.nome,
                        vacinaAbreviacao: this.local.vacina.abreviacao,
                        vacinaSigla: this.local.vacina.sigla,
                        vacinaSiglaNome: `${this.local.vacina.sigla} - ${this.local.vacina.nome}`,
                        vacinaSiglaAbreviacao: `${this.local.vacina.sigla} - ${this.local.vacina.abreviacao}`,
                        vacinaAbreviacaoNome: `${this.local.vacina.abreviacao} - ${this.local.vacina.nome}`,
                        vacinaSiglaAbreviacaoNome: `${this.local.vacina.sigla} - ${this.local.vacina.abreviacao} -
                            ${this.local.vacina.nome}`,
                        lote: {
                            text: lote.text,
                            value: lote.value,
                        },
                        quantidade: parseInt(this.local.quantidade),
                    })

                    this.resetRkmFormVacina()
                })
        },
        handleLimpar() {
            this.resetRkmFormVacina()
        },
        resetRkmFormVacina() {
            this.local = {
                vacina: null,
                lote: null,
                estoque: null,
                quantidade: null,
            }

            this.$refs.RkmFormVacina.resetValidator()
        },
        handleDestroy(index) {
            this.showDeleteConfirmation('Deseja realmente excluir a vacina?', async () => this.model.vacinas.splice(index, 1))
        },
        save() {
            this.$refs.RkmFormDistribuicao.validate()
                .then(valid => {
                    if (valid === false) return

                    if (this.model.vacinas.length === 0) {
                        this.showValidationNotification('Obrigatório incluir pelo menos uma vacina.')
                        return
                    }

                    const request = {
                        salaVacinacaoIdDe: this.model.salaVacinacaoIdDe,
                        salaVacinacaoIdPara: this.model.salaVacinacaoIdPara,
                        data: this.others.moment(this.model.data).format('DD/MM/YYYY'),
                        vacinas: this.model.vacinas.map(vacina => {
                            return {
                                vacinaId: vacina.vacinaId,
                                lote: vacina.lote.value.lote,
                                dataValidadeLote: vacina.lote.value.dataValidadeLote,
                                quantidade: vacina.quantidade,
                            }
                        }),
                    }

                    this.setLoading()

                    this.repositories.distribuicao.store(this.camelObjectKeysToSnakeRecursive(request))
                        .then(response => {
                            this.showSaveNotification()
                            this.$emit('save', response)
                        })
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                })
        },
        getLotes(salaVacinacaoId, vacinaId) {
            const request = {
                listar: 'lotes',
                salaVacinacaoId: salaVacinacaoId,
                vacinaId: vacinaId,
            }

            this.setLoading()

            this.repositories.distribuicao.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.lotes = response.data.map(lote => {
                        return {
                            text: `${lote.lote} - Val: ${this.others.moment(lote.dataValidadeLote).format('DD/MM/YYYY')}`,
                            value: lote,
                        }
                    })

                    if (this.form.lotes.length > 0) {
                        const selected = this.form.lotes[0]
                        this.local.lote = selected.value

                        this.getEstoque(this.model.salaVacinacaoIdDe, this.local.vacina.id, this.local.lote.lote, this.local.lote.dataValidadeLote)
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getEstoque(salaVacinacaoId, vacinaId, lote, dataValidadeLote) {
            const request = {
                listar: 'estoque',
                salaVacinacaoId: salaVacinacaoId,
                vacinaId: vacinaId,
                lote: lote,
                dataValidadeLote: dataValidadeLote,
            }

            this.setLoading()

            this.repositories.distribuicao.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.local.estoque = response.data
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        autocompleteTextField(item) {

            let textField = null

            switch (this.additionals.parametrizacao.parametrizacaoDescricaoVacinaId) {
                case this.enums.parametrizacaoDescricaoVacina.DESCRICAO_ABREVIADA:
                    textField = `${item.abreviacao}`
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA:
                    textField = `${item.sigla}`
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO:
                    textField = `${item.sigla} - ${item.nome}`
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO_ABREVIADA:
                    textField = `${item.sigla} - ${item.abreviacao}`
                    break;

                case this.enums.parametrizacaoDescricaoVacina.DESCRICAO_ABREVIADA_DESCRICAO:
                    textField = `${item.abreviacao} - ${item.nome}`
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO_ABREVIADA_DESCRICAO:
                    textField = `${item.sigla} - ${item.abreviacao} - ${item.nome}`
                    break;

                default:
                    textField = `${item.nome}`
                    break;
            }

            return textField
        },
    },
    mixins: [ error, repository, stringCase ],
    mounted() {
        this.load()
    },
}
</script>